<!-- eslint-disable no-console -->
<script setup lang="ts">
import type { FetchError } from 'ofetch'
import { ofetch } from 'ofetch'
import { RecaptchaV2 } from 'vue3-recaptcha-v2'

import { useForm } from 'vee-validate'
import * as y from 'yup'
import { toTypedSchema } from '@vee-validate/yup'
import type { RError } from 'missw-form/src/response'
import countries from '~/assets/countries.json'

countries.sort((a, b) => a.name.localeCompare(b.name))

useHead({
  meta: [
    {
      name: 'description',
      content: 'Miss W - Take a Chance And WIN 1499 BTC',
    },
  ],
})

const widgetId = ref<number>()
const recaptcha = ref<string>()
const error = ref<string>()
const success = ref<string>()

const { meta, errors, values, isSubmitting, handleSubmit, defineField } = useForm({
  validationSchema: toTypedSchema(y.object({
    fullName: y.string().required(),
    nationality: y.string().oneOf(countries.map(c => c.code), () => 'Must be a valid country').required(),
    dateOfBirth: y.date().required(),
    email: y.string().email(),
    phoneNumber: y.string().required(),
    currentCity: y.string().required(),
    languageSpoken: y.string().required(),
    profession: y.string().required(),
    education: y.string().required(),
    socialMediaProfiles: y.string().required(),
    cryptoWalletAddress: y.string().optional(),
    briefBiography: y.string().required(),
  })),
})
const nationalityObj = computed(() => countries.find(country => country.code === values.nationality))

const ticket = computed(() => {
  const now = new Date()
  const year = now.getFullYear()
  const month = now.getMonth() + 1
  return `MISSW${
    countries.find(({ code }) => code === values.nationality)?.name
  }${
    year.toString().substring(2)}${month.toString().padStart(2, '0')
  }${
    success.value?.toString().padStart(2, '0') ?? '00'
  }`
})

watch(() => meta.value.dirty, () => {
  error.value = undefined
})

const fullBodyShot = ref<File[]>()
const headshot = ref<File[]>()

const [fullName, fullNameAttr] = defineField('fullName')
const [nationality] = defineField('nationality')
const [dateOfBirth, dateOfBirthAttr] = defineField('dateOfBirth')
const [email, emailAttr] = defineField('email')
const [phoneNumber, phoneNumberAttr] = defineField('phoneNumber')
const [currentCity, currentCityAttr] = defineField('currentCity')
const [languageSpoken, languageSpokenAttr] = defineField('languageSpoken')
const [profession, professionAttr] = defineField('profession')
const [education, educationAttr] = defineField('education')
const [socialMediaProfiles, socialMediaProfilesAttr] = defineField('socialMediaProfiles')
const [cryptoWalletAddress, cryptoWalletAddressAttr] = defineField('cryptoWalletAddress')
const [briefBiography, briefBiographyAttr] = defineField('briefBiography')

const formRef = ref()

const onSubmit = handleSubmit(async (values) => {
  console.info('submit', values)
  const formData = new FormData()
  for (const key in values)
    formData.append(key, values[key])
  if (!headshot.value?.length || !fullBodyShot.value?.length) {
    error.value = 'Please upload head shot and full body shot!'
    return
  }
  formData.append('headshot', headshot.value[0])
  formData.append('fullBodyShot', fullBodyShot.value[0])
  formData.append('recaptcha', recaptcha.value!)
  formData.set('cryptoWalletAddress', values.cryptoWalletAddress ?? '')
  console.info('send request')
  await ofetch('/application', {
    baseURL: import.meta.env.VITE_APP_API_URL,
    body: formData,
    method: 'POST',
  }).then((resp) => {
    console.info('response', resp)
    if (!resp.success)
      throw new Error(resp)
    error.value = undefined
    success.value = resp.data.id
  }).catch(async (err: FetchError) => {
    console.warn('catch error', err)
    if (err && typeof err === 'object') {
      if ('response' in err) {
        const data = err.response?._data
        if (data && typeof data === 'object' && 'success' in data) {
          const resp = data as RError
          error.value = resp.message
          return
        }
      }
      else if ('success' in err) {
        const resp = err as RError
        error.value = resp.message
        return
      }
    }
    throw err
  }).catch((err) => {
    error.value = err && typeof err === 'object' && 'message' in err ? err.message : err
  })
}, ({ errors }) => {
  console.warn('errors', errors)
})
</script>

<template>
  <div w-full overflow-x-hidden>
    <MissWHeader
      cover="/images/image-167-join.webp"
      title="REGISTER NOW"
      subtitle="TAKE A CHANCE AND WIN 1499 BTC"
      small-title-size="40px"
      translate-y="-20px"
    >
      <form ref="formRef" :disabled="success" mx-27px mt-40px max-w-600px rounded-4 p-17px sm:mx-auto sm:mt-120px sm:p-32px class="bg-#000" @submit="onSubmit">
        <h2 text-15px font-bold sm:text-24px>
          Submit your application
        </h2>

        <div class="field" mt-14px sm:mt-32px>
          <label for="fullname">Full Name</label>
          <InputText id="fullname" v-model="fullName" :disabled="!!success" v-bind="fullNameAttr" required placeholder="Enter your full name" />
          <InlineMessage v-if="errors.fullName" severity="error">
            {{ errors.fullName }}
          </InlineMessage>
        </div>

        <div class="field" mt-8px sm:mt-16px>
          <label for="nationality">Nationality</label>
          <Dropdown v-model="nationality" editable :disabled="!!success" :options="countries" option-value="code" option-label="name" placeholder="Select your phone nationality" class="w-full md:w-14rem">
            <template #value="slotProps">
              <div v-if="nationalityObj" class="align-items-center flex">
                <img :alt="nationalityObj.name" :src="nationalityObj.image" class="mr-2" style="width: 18px">
                <div>{{ nationalityObj.name }}</div>
              </div>
              <span v-else>
                {{ slotProps.placeholder }}
              </span>
            </template>
            <template #option="slotProps">
              <div class="align-items-center flex">
                <img :alt="slotProps.option.name" :src="slotProps.option.image" :class="`mr-2 flag flag-${slotProps.option.code.toLowerCase()}`" style="width: 18px">
                <div>{{ slotProps.option.name }}</div>
              </div>
            </template>
          </Dropdown>
          <InlineMessage v-if="errors.nationality" severity="error">
            {{ errors.nationality }}
          </InlineMessage>
        </div>

        <div class="field" mt-8px sm:mt-16px>
          <label for="dateOfBirth">Date of Birth</label>
          <Calendar id="dateOfBirth" v-model="dateOfBirth" :disabled="!!success" v-bind="dateOfBirthAttr" required aria-autocomplete="dateOfBirth" placeholder="Select your birth date" show-icon touch-u-i icon-display="input" />
          <InlineMessage v-if="errors.dateOfBirth" severity="error">
            {{ errors.dateOfBirth }}
          </InlineMessage>
        </div>

        <div class="field" mt-8px sm:mt-16px>
          <label for="email">Email Address</label>
          <Password id="email" v-model="email" :disabled="!!success" v-bind="emailAttr" autocomplete="email" required toggle-mask :feedback="false" placeholder="Enter your email address" />
          <InlineMessage v-if="errors.email" severity="error">
            {{ errors.email }}
          </InlineMessage>
        </div>

        <div class="field" mt-8px sm:mt-16px>
          <label for="phoneNumber">Phone Number</label>
          <Password id="phoneNumber" v-model="phoneNumber" :disabled="!!success" v-bind="phoneNumberAttr" autocomplete="phone" required toggle-mask :feedback="false" placeholder="Enter your phone number" />
          <InlineMessage v-if="errors.phoneNumber" severity="error">
            {{ errors.phoneNumber }}
          </InlineMessage>
        </div>

        <div class="field" mt-8px sm:mt-16px>
          <label for="currentCity">Current City</label>
          <InputText id="currentCity" v-model="currentCity" :disabled="!!success" v-bind="currentCityAttr" required placeholder="Enter the city you currently reside in" />
          <InlineMessage v-if="errors.currentCity" severity="error">
            {{ errors.currentCity }}
          </InlineMessage>
        </div>

        <!--  -->

        <div class="field" mt-60px>
          <label for="profession">Profession</label>
          <InputText id="profession" v-model="profession" :disabled="!!success" v-bind="professionAttr" required placeholder="Enter your profession" />
          <InlineMessage v-if="errors.profession" severity="error">
            {{ errors.profession }}
          </InlineMessage>
        </div>

        <div class="field" mt-8px sm:mt-16px>
          <label for="education">Education</label>
          <InputText id="education" v-model="education" :disabled="!!success" v-bind="educationAttr" required placeholder="Enter your highest qualifications" />
          <InlineMessage v-if="errors.education" severity="error">
            {{ errors.education }}
          </InlineMessage>
        </div>

        <div class="field" mt-8px sm:mt-16px>
          <label for="languages">Languages Spoken</label>
          <InputText id="languages" v-model="languageSpoken" :disabled="!!success" v-bind="languageSpokenAttr" required placeholder="Enter languages spoken" />
          <InlineMessage v-if="errors.languageSpoken" severity="error">
            {{ errors.languageSpoken }}
          </InlineMessage>
        </div>

        <!--  -->

        <div class="field" mt-60px>
          <label for="socialMediaProfiles">Social Media Profiles</label>
          <InputText id="socialMediaProfiles" v-model="socialMediaProfiles" :disabled="!!success" v-bind="socialMediaProfilesAttr" required placeholder="Enter your social URLs, separately by a comma." />
          <InlineMessage v-if="errors.socialMediaProfiles" severity="error">
            {{ errors.socialMediaProfiles }}
          </InlineMessage>
        </div>

        <div class="field" mt-8px sm:mt-16px>
          <label for="cryptoWalletAddress">Crypto Wallet Address (Optional)</label>
          <InputText id="cryptoWalletAddress" v-model="cryptoWalletAddress" :disabled="!!success" v-bind="cryptoWalletAddressAttr" placeholder="Enter your wallet address" />
          <InlineMessage v-if="errors.cryptoWalletAddress" severity="error">
            {{ errors.cryptoWalletAddress }}
          </InlineMessage>
        </div>

        <!--  -->

        <div class="field" mt-60px>
          <label for="headshot">Upload Headshot</label>
          <FileUpload :disabled="!!success" choose-label="Upload your headshot (Click here)" mode="basic" required name="headshot" accept="image/*" :max-file-size="12000000" @select="event => headshot = event.files" />
          <InlineMessage v-if="!headshot?.length && meta.touched">
            Please Upload your headshot
          </InlineMessage>
        </div>

        <div class="field" mt-8px sm:mt-16px>
          <label for="fullBodyShot">Upload Full Body Shot</label>
          <FileUpload :disabled="!!success" choose-label="Upload your body shot (Click here)" mode="basic" required name="fullBodyShot" accept="image/*" :max-file-size="12000000" @select="event => fullBodyShot = event.files" />
          <InlineMessage v-if="!fullBodyShot?.length && meta.touched">
            Please Upload your body shot
          </InlineMessage>
        </div>

        <div class="field" mt-8px sm:mt-16px>
          <label for="briefBiography">Brief Biography</label>
          <InputText id="briefBiography" v-model="briefBiography" :disabled="!!success" v-bind="briefBiographyAttr" required placeholder="Write something about yourself" />
          <InlineMessage v-if="errors.briefBiography" severity="error">
            {{ errors.briefBiography }}
          </InlineMessage>
        </div>

        <div mx-auto text-center>
          <p mt-24px text-10px font-bold sm:text-18px>
            By clicking submit, you agree to the following:
          </p>

          <div mx-auto max-w-330px>
            <p class="text-#88888c" mt-8px text-6px sm:text-12px>
              Terms & Conditions: I have read and agree to the Terms and Conditions of the Miss W Contest.
            </p>
            <p class="text-#88888c" mt-8px text-6px sm:text-12px>
              Privacy Policy: I consent to the collection and use of my personal data in accordance with the Privacy Policy.
            </p>
          </div>

          <RecaptchaV2
            v-if="!success"
            class="g-recaptcha"
            @widget-id="id => widgetId = id"
            @error-callback="recaptcha = undefined"
            @expired-callback="recaptcha = undefined"
            @load-callback="resp => {
              if (typeof resp === 'string') recaptcha = resp
              else recaptcha = undefined
            }"
          />

          <InlineMessage v-if="!recaptcha && !success" severity="warn" mt-4>
            Please pass verification
          </InlineMessage>
          <InlineMessage v-if="error" mt-8 class="mx-auto block b-1 b-red text-white" severity="error">
            <span opacity-70>Response Error:</span>
            <br>
            <span ml-2 font-bold>
              {{ error }}
            </span>
          </InlineMessage>
          <InlineMessage v-if="success" mt-8 class="mx-auto block b-1 b-green text-white" severity="success">
            <span text-green font-bold>
              Submitted successfully!
            </span><br>
            Your Registration Ticket is:
            <span mx-1 rounded bg-gray-800 px-2 font-bold>{{ ticket }}</span>.
            Thank you for your application, a MissW Team Member will reach out to you upon successful review. Please do not submit multiple applications.
          </InlineMessage>

          <Button
            v-if="!success"
            :loading="isSubmitting"
            :disabled="!recaptcha || !meta.touched || !meta.valid"
            class="p-[11px_8px] lh-100% sm:p-[18px_20px]" mx-auto mt-30px block btn-primary w-full rounded-full uppercase lt-sm:text-10px
            @click="(e) => onSubmit(e)"
          >
            SUBMIT MISS W APPLICATION
          </Button>
        </div>
      </form>
    </MissWHeader>

    <IntroductionSponsors lt-sm:pt-100px />
  </div>
</template>

<style scoped lang="scss">
form {
  :deep(.p-inline-message-text) {
    @apply "lt-sm:text-10px!";
  }
  .field {
    @apply col;
    label {
      @apply font-bold text-9.6px sm:text-18px lh-130%;
    }
    .p-fileupload {
      @apply mt-16px;
    }
    :deep(.p-fileupload .p-button) {
      width: 100%;
      @apply bg-#1b0808 rounded-full text-#88888C p-[9px_10px] sm:p-[17px_20px] lt-sm:text-10px;
      .p-button-label {
        @apply text-start lt-sm:text-10px;
      }
    }
    :deep(.p-password) {
      @apply mt-16px bg-#1b0808 rounded-full;
      input {
        outline-color: rgb(63, 4, 4) !important;
        outline-width: 0px;
        &:focus-visible {
          outline-style: groove;
          outline-width: 1px;
        }
        background: transparent;
        @apply p-[9px_10px] sm:p-[17px_20px] w-full rounded-full lt-sm:text-10px;
        &::placeholder {
          @apply lt-sm:text-10px;
        }
      }
    }
    :deep(.p-calendar) {
      input {
        outline-color: rgb(63, 4, 4) !important;
        outline-width: 0px;
        &:focus-visible {
          outline-style: groove;
          outline-width: 1px;
        }
        @apply mt-16px bg-#1b0808 p-[9px_10px] sm:p-[17px_20px] rounded-full lt-sm:text-10px;
        &::placeholder {
          @apply lt-sm:text-10px;
        }
      }
      .p-input-icon {
        margin-top: -2px;
      }
    }
    input {
      outline-color: rgb(63, 4, 4) !important;
      outline-width: 0px;
      &:focus-visible {
        outline-style: groove;
        outline-width: 1px;
      }
      @apply mt-16px bg-#1b0808 p-[9px_10px] sm:p-[17px_20px] rounded-full lt-sm:text-10px;
      &::placeholder {
        @apply lt-sm:text-10px;
      }
    }
    :deep(.p-input-icon) {
      @apply mr-2 sm:size-20px mt--10px;
    }

    .p-dropdown {
      :deep() {
        .p-inputtext {
          padding: 0px !important;
          @apply lt-sm:text-10px outline-none;
        }
        .p-dropdown-trigger {
          height: auto !important;
          width: 1.25rem !important;
          @apply "lt-sm:w-2.25rem!";
        }
      }
      outline-color: rgb(63, 4, 4) !important;
      @apply mt-16px bg-#1b0808 p-[9px_10px] sm:p-[17px_20px] w-full rounded-full lt-sm:text-10px;
    }
  }
}

.g-recaptcha {
  margin-top: 1.5rem;
  :deep() & > div {
    margin: auto !important;
  }
}

.p-inline-message {
  background: transparent;
  justify-content: flex-start;
}
</style>
